import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import axiosInstance from '../api/axiosInstance'
import logger from '../utils/logger'

// ✅ Fetch Data Hook
export const useFetchData = (key, url, enabled = true) => {
  return useQuery({
    queryKey: [key], // Unique cache key
    queryFn: async () => {
      const response = await axiosInstance.get(url)
      return response.data
    },
    enabled, // Allow conditional fetching
  })
}

// ✅ Create Data (POST Request)
export const useCreateData = (url, queryKeyToInvalidate) => {
  const queryClient = useQueryClient()

  const {
    mutate,
    isPending,
    isLoading,
    error,
    data, // <-- add this line
  } = useMutation({
    mutationKey: [url],
    mutationFn: (data) => axiosInstance.post(url, data).then((res) => res.data),
    onSuccess: () => {
      if (queryKeyToInvalidate) {
        queryClient.invalidateQueries([queryKeyToInvalidate])
      }
    },
    onError: (error) => {
      logger.error('Error creating data:', error.response?.data || error.message)
    },
  })

  return { mutate, isPending, isLoading, error, data } // <-- return data here
}

// ✅ Update Data (PUT Request)
export const useUpdateData = (url, queryKeyToInvalidate) => {
  const queryClient = useQueryClient() // Access the query client
  const { mutate, isPending, isLoading, error } = useMutation({
    mutationKey: [url],
    mutationFn: (data) => axiosInstance.patch(url, data).then((res) => res.data),
    onSuccess: () => {
      if (queryKeyToInvalidate) {
        queryClient.invalidateQueries([queryKeyToInvalidate])
      }
    },
  })

  return { mutate, isPending, isLoading, error }
}

// ✅ Delete Data (DELETE Request)
export const useDeleteData = (url, queryKeyToInvalidate) => {
  const queryClient = useQueryClient() // Access the query client
  const { mutate, isPending, isLoading, error } = useMutation({
    mutationKey: [url],
    mutationFn: () => axiosInstance.delete(url).then((res) => res.data),
    onSuccess: () => {
      // ✅ Revalidate users API cache
      if (queryKeyToInvalidate) {
        queryClient.invalidateQueries([queryKeyToInvalidate])
      }
    },
  })
  return { mutate, isPending, isLoading, error }
}
